import React, { useState, useEffect } from 'react'
import ProgressCircle from '../shared/ProgressCircle'
import { format, parse } from 'date-fns'
import classNames from 'classnames'

const PaymentPlanSummary = ({ paymentPlan }) => {
  const [showPaymentDateForm, setShowPaymentDateForm] = useState(false)
  const [selectedDate, setSelectedDate] = useState('')
  const [dateError, setDateError] = useState('')
  const [optimisticPaymentDate, setOptimisticPaymentDate] = useState(null)
  const [updateError, setUpdateError] = useState(null)

  const paymentAlertClasses = classNames({
    'alert': true,
    'alert-success-muted': true,
    'd-flex': true,
    'align-items-center': true,
    'rounded-top-end-0': true,
    'rounded-bottom-start-0': true,
    'border': true,
    'border-success': true,
    'payment-date-updated': optimisticPaymentDate,
  })

  // Listen for payment date update events from Stimulus controller
  useEffect(() => {
    const handlePaymentDateUpdate = event => {
      const { formattedDate } = event.detail

      // Set optimistic update
      setOptimisticPaymentDate(formattedDate)
      setUpdateError(null)
      setShowPaymentDateForm(false)
      setSelectedDate('')
      setDateError('')
    }

    const handlePaymentDateSuccess = _event => {
      setUpdateError(null)
    }

    const handlePaymentDateError = event => {
      const { message } = event.detail
      setOptimisticPaymentDate(null) // Revert optimistic update
      setUpdateError(message)
    }

    // Listen for events from Stimulus controller
    document.addEventListener(
      'subscription-payment-date:paymentDateUpdated',
      handlePaymentDateUpdate
    )
    document.addEventListener('subscription-payment-date:showSuccess', handlePaymentDateSuccess)
    document.addEventListener('subscription-payment-date:showError', handlePaymentDateError)

    return () => {
      document.removeEventListener(
        'subscription-payment-date:paymentDateUpdated',
        handlePaymentDateUpdate
      )
      document.removeEventListener(
        'subscription-payment-date:showSuccess',
        handlePaymentDateSuccess
      )
      document.removeEventListener('subscription-payment-date:showError', handlePaymentDateError)
    }
  }, [])

  // Get the current payment date (optimistic or original)
  const getCurrentPaymentDate = () => {
    return optimisticPaymentDate || paymentPlan?.next_payment_date
  }

  if (!paymentPlan) return null

  const getNextPaymentDate = () => {
    if (!paymentPlan.next_payment_date) {
      return null
    }

    try {
      const paymentDateString = paymentPlan.next_payment_date
      const parsedDate = parse(paymentDateString, 'MMM d, yyyy', new Date())

      return parsedDate
    } catch {
      return null
    }
  }

  const getDateConstraints = () => {
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    // Min date is always tomorrow (no past dates or today)
    const minDate = tomorrow.toISOString().split('T')[0]

    // Max date is the day before next payment (to ensure 24+ hour gap)
    const nextPaymentDate = getNextPaymentDate()
    let maxDate = null

    if (nextPaymentDate) {
      const dayBeforePayment = new Date(nextPaymentDate)
      dayBeforePayment.setDate(dayBeforePayment.getDate() - 1)

      // Only set max date if it's after tomorrow (otherwise no valid dates exist)
      if (dayBeforePayment >= tomorrow) {
        maxDate = dayBeforePayment.toISOString().split('T')[0]
      }
    }

    return { minDate, maxDate }
  }

  const validateSelectedDate = dateString => {
    if (!dateString) {
      setDateError('')
      return true
    }

    const selectedDate = new Date(dateString)
    const today = new Date()

    // Reset time to beginning of day for date-only comparison
    today.setHours(0, 0, 0, 0)
    selectedDate.setHours(0, 0, 0, 0)

    if (selectedDate < today) {
      setDateError('Please select a future date')
      return false
    }

    const nextPaymentDate = getNextPaymentDate()
    if (nextPaymentDate) {
      nextPaymentDate.setHours(0, 0, 0, 0)

      const timeDifference = Math.abs(selectedDate - nextPaymentDate)
      const hoursDifference = timeDifference / (1000 * 60 * 60)

      if (hoursDifference < 24) {
        const formattedPaymentDate = format(nextPaymentDate, 'MMMM d, yyyy')
        setDateError(
          `Please select a date that is at least 24 hours away from your next payment date (${formattedPaymentDate})`
        )
        return false
      }
    }

    setDateError('')
    return true
  }

  const handleDateChange = e => {
    const newDate = e.target.value
    setSelectedDate(newDate)
    validateSelectedDate(newDate)
  }

  const handlePaymentDateCancel = () => {
    setShowPaymentDateForm(false)
    setSelectedDate('')
    setDateError('')
  }

  const {
    total_amount_customer_pays,
    balance_remaining,
    total_savings,
    previous_payment_date,
    next_payment_amount,
    next_payment_date,
    payment_history,
    progress_percentage,
    last_payment_date,
    total_payments_count,
    payments_remaining,
    is_subscription_active,
    is_subscription_upcoming,
    subscription_needs_attention,
    subscription_latest_invoice_url,
  } = paymentPlan

  const categorizeError = errorMessage => {
    const message = errorMessage?.toLowerCase() || ''

    if (
      message.includes('permission') ||
      message.includes('forbidden') ||
      message.includes('unauthorized')
    ) {
      return {
        type: 'permission',
        title: 'Permission Error',
        icon: 'shield-x',
        suggestion:
          'Please refresh the page and try again. If the problem persists, contact support.',
      }
    } else if (message.includes('not found') || message.includes('refresh')) {
      return {
        type: 'not_found',
        title: 'Data Not Found',
        icon: 'search',
        suggestion: 'Please refresh the page and try again.',
      }
    } else if (message.includes('rate limit') || message.includes('too many requests')) {
      return {
        type: 'rate_limit',
        title: 'Too Many Requests',
        icon: 'clock',
        suggestion: 'Please wait a moment and try again.',
      }
    } else if (
      message.includes('service') ||
      message.includes('unavailable') ||
      message.includes('connection')
    ) {
      return {
        type: 'service',
        title: 'Service Temporarily Unavailable',
        icon: 'wifi-off',
        suggestion:
          'Our payment service is temporarily unavailable. Please try again in a few minutes.',
      }
    } else if (
      message.includes('invalid') ||
      message.includes('date') ||
      message.includes('24 hours')
    ) {
      return {
        type: 'validation',
        title: 'Invalid Date Selection',
        icon: 'calendar-x',
        suggestion:
          'Please select a valid future date that is at least 24 hours away from your next payment.',
      }
    } else {
      return {
        type: 'general',
        title: 'Update Failed',
        icon: 'alert-circle',
        suggestion: 'Please try again. If the problem continues, contact support.',
      }
    }
  }

  return (
    <div className='border-0 mb-4'>
      <h3 className='mb-4'>Payment Plan</h3>

      {(is_subscription_active || is_subscription_upcoming) &&
        next_payment_amount &&
        next_payment_date && (
          <>
            <div className={paymentAlertClasses} role='alert'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                fill='currentColor'
                className='bi flex-shrink-0 me-2'
                viewBox='0 0 16 16'
                role='img'
                aria-label='Success:'
              >
                <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z' />
              </svg>
              <div className='text-body'>
                {next_payment_amount} auto-pay scheduled for {getCurrentPaymentDate()}.
              </div>
            </div>

            <div>
              <div>
                <span>Upcoming payment: {next_payment_amount}</span>
              </div>
              <div>
                <span className='text-muted small'>Due {getCurrentPaymentDate()}</span>
              </div>
            </div>

            <div className='mt-4'>
              <a
                href={`/customer_documents/${paymentPlan.document_id}/setup_additional_payment_amount`}
                className='btn btn-outline-primary w-100 shadow-none text-wrap'
              >
                Make an additional payment
              </a>
              <button
                className='btn btn-link w-100 mt-2 text-decoration-underline'
                onClick={() => setShowPaymentDateForm(prev => !prev)}
              >
                Edit autopay
              </button>
            </div>
          </>
        )}

      {/* Show update error if payment date change failed */}
      {updateError && (
        <div className='alert alert-danger mt-3' role='alert'>
          <div className='d-flex align-items-start'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='20'
              height='20'
              fill='currentColor'
              className='bi flex-shrink-0 me-2 mt-1'
              viewBox='0 0 16 16'
              role='img'
              aria-label='Error:'
            >
              {(() => {
                const errorInfo = categorizeError(updateError)
                switch (errorInfo.icon) {
                  case 'shield-x':
                    return (
                      <>
                        <path d='M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 0 1-2.517 2.453 7.159 7.159 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7.158 7.158 0 0 1-1.048-.625 11.777 11.777 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 62.456 62.456 0 0 1 5.072.56z' />
                        <path d='M6.5 5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v1.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5V5zm3 4.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-1.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v1.5z' />
                      </>
                    )
                  case 'search':
                    return (
                      <path d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z' />
                    )
                  case 'clock':
                    return (
                      <>
                        <path d='M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z' />
                        <path d='M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z' />
                      </>
                    )
                  case 'wifi-off':
                    return (
                      <path d='M10.706 3.294A12.545 12.545 0 0 0 8 3C5.259 3 2.723 3.882.663 5.379a.485.485 0 0 0-.048.736.518.518 0 0 0 .668.05A11.448 11.448 0 0 1 8 4c.63 0 1.249.05 1.852.148l.854-.854zM8 6c-1.905 0-3.68.56-5.166 1.526a.48.48 0 0 0-.063.745.525.525 0 0 0 .652.065 8.448 8.448 0 0 1 3.51-1.27L8 6zm2.596 1.404.785-.785c.63.24 1.227.545 1.785.907a.482.482 0 0 1 .063.745.525.525 0 0 1-.652.065 8.462 8.462 0 0 0-1.98-.932zM8 10l.933-.933a6.455 6.455 0 0 1 2.013.637c.285.145.326.524.1.75l-.015.015a.532.532 0 0 1-.611.09A5.478 5.478 0 0 0 8 10zm4.905-4.905.747-.747c.59.3 1.153.645 1.685 1.03a.485.485 0 0 1 .047.737.518.518 0 0 1-.668.05 11.493 11.493 0 0 0-1.811-1.07zM9.02 11.78c.238.14.236.464.04.66l-.707.706a.5.5 0 0 1-.707 0l-.707-.707c-.195-.195-.197-.518.04-.66A1.99 1.99 0 0 1 8 11.5c.374 0 .723.102 1.021.28zm4.355-9.905a.53.53 0 0 1 .75.75l-10.75 10.75a.53.53 0 0 1-.75-.75l10.75-10.75z' />
                    )
                  case 'calendar-x':
                    return (
                      <>
                        <path d='M6.146 7.146a.5.5 0 0 1 .708 0L8 8.293l1.146-1.147a.5.5 0 1 1 .708.708L8.707 9l1.147 1.146a.5.5 0 0 1-.708.708L8 9.707l-1.146 1.147a.5.5 0 0 1-.708-.708L7.293 9 6.146 7.854a.5.5 0 0 1 0-.708z' />
                        <path d='M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z' />
                      </>
                    )
                  default:
                    return (
                      <path d='M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z' />
                    )
                }
              })()}
            </svg>
            <div className='flex-grow-1'>
              <div className='fw-bold'>{categorizeError(updateError).title}</div>
              <div className='mb-2'>{updateError}</div>
              <div className='text-muted small'>{categorizeError(updateError).suggestion}</div>
            </div>
          </div>
        </div>
      )}

      {subscription_needs_attention && next_payment_amount && previous_payment_date && (
        <div
          className='alert alert-danger-muted d-flex align-items-center rounded-top-end-0 rounded-bottom-start-0 border border-danger'
          role='alert'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            fill='currentColor'
            className='bi flex-shrink-0 me-2'
            viewBox='0 0 16 16'
            role='img'
            aria-label='Danger:'
          >
            <path d='M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z' />
          </svg>
          <div className='text-body'>
            Failed autopay of {next_payment_amount} on {previous_payment_date}. If you fail to make
            this payment, you will be in default.{' '}
            <a
              href={subscription_latest_invoice_url}
              target='_blank'
              rel='noopener noreferrer'
              className='text-decoration-underline text-primary'
            >
              Click here to update your payment method.
            </a>
          </div>
        </div>
      )}

      {/* Payment Date Form */}
      {showPaymentDateForm && (
        <div className='card border-primary mt-3'>
          <div className='card-body'>
            <h4 className='card-title font-serif'>Change Payment Date</h4>
            <form
              data-controller='subscription-payment-date'
              data-subscription-payment-date-document-id-value={paymentPlan.document_id}
              data-subscription-payment-date-payment-amount-value={next_payment_amount}
              data-action='submit->subscription-payment-date#submitPaymentDateChange'
            >
              <div className='mb-3'>
                <label htmlFor='payment-date' className='form-label'>
                  Select new payment date:
                </label>
                <input
                  type='date'
                  id='payment-date'
                  className='form-control'
                  value={selectedDate}
                  onChange={handleDateChange}
                  min={getDateConstraints().minDate}
                  max={getDateConstraints().maxDate}
                  data-subscription-payment-date-target='dateInput'
                  required
                />
                <div className='form-text text-muted'>
                  {getNextPaymentDate()
                    ? `Must be at least 1 day before your next payment (${getCurrentPaymentDate()})`
                    : 'Select a future date for your new payment schedule. You will be charged the next payment amount on this date.'}
                </div>
              </div>
              {dateError && <div className='text-danger mb-3'>{dateError}</div>}
              <div className='d-flex flex-column flex-sm-row gap-2'>
                <button
                  type='submit'
                  className='btn btn-primary flex-fill text-wrap'
                  disabled={!selectedDate || dateError}
                >
                  Update Payment Date
                </button>
                <button
                  type='button'
                  className='btn btn-outline-secondary flex-fill'
                  onClick={handlePaymentDateCancel}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <hr className='text-muted opacity-25 my-4' />

      {payment_history && payment_history.length > 0 && (
        <div>
          <h4 className='mb-4'>Transactions</h4>
          {payment_history.map(payment => (
            <div key={payment.id} className='d-flex justify-content-between mb-2'>
              <span>
                {payment.amount} paid on {payment.date}
              </span>
            </div>
          ))}
          <hr className='text-muted opacity-25 my-4' />
        </div>
      )}

      <div>
        <h4 className='mb-4'>Payment Plan Summary</h4>

        <div className='row mb-4 gy-4'>
          <div className='col-md-8'>
            <div className='d-flex justify-content-between mb-2'>
              <span>Total payment amount</span>
              <span>{total_amount_customer_pays}</span>
            </div>
            <div className='d-flex justify-content-between mb-2'>
              <span>Balance remaining</span>
              <span>{balance_remaining}</span>
            </div>
            {total_savings && (
              <div className='d-flex justify-content-between mb-2'>
                <span>Total savings</span>
                <span>{total_savings}</span>
              </div>
            )}
            <div className='d-flex justify-content-between mb-2'>
              <span>Payments</span>
              <span>{total_payments_count}</span>
            </div>
            <div className='d-flex justify-content-between mb-2'>
              <span>Payments remaining</span>
              <span>{payments_remaining}</span>
            </div>
            {is_subscription_active && (
              <div className='d-flex justify-content-between'>
                <span>Last payment</span>
                <span>{last_payment_date}</span>
              </div>
            )}
          </div>

          <div className='col-md-4 text-center'>
            <ProgressCircle titleText='Paid' percentage={progress_percentage} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentPlanSummary
